import { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import KeyboardCommandKeyRoundedIcon from '@mui/icons-material/KeyboardCommandKeyRounded';
import BaseInput from "../BaseInput";
import InputAutoCompleteCard from "../components/InputAutoCompleteCard";

const normalizeText = (text) => {
    if (text === null || text === undefined) return '';
    return String(text)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, ''); // Remove diacríticos (acentos)
};

const SelectOptionsInput = ({ value = [], onInput, label, placeholder, options = [], allowNewValues = false, maxItems, className, variant = 'light', autoScroll = true, collapseInAmount = null, ...props }) => {
    const { t } = useTranslation('fragments');
    const [selectedValues, setSelectedValues] = useState(Array.isArray(value) ? value : []);
    const [inputText, setInputText] = useState("");
    const [autocompleteOpen, setAutocompleteOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [isFocused, setIsFocused] = useState(false);
    const [manuallyExpanded, setManuallyExpanded] = useState(false);

    const containerRef = useRef();
    const inputRef = useRef();
    const optionClickedRef = useRef(false);
    const blurTimeoutRef = useRef(null);
    const isFocusedRef = useRef(false);

    // Reset manual expansion when focused (so next blur defaults to collapsed)
    useEffect(() => {
        if (isFocused) {
            setManuallyExpanded(false);
        }
    }, [isFocused]);

    // Verificar se o limite de itens foi atingido
    const isMaxItemsReached = maxItems !== undefined && selectedValues.length >= maxItems;

    useEffect(() => {
        if (Array.isArray(value)) {
            setSelectedValues(value);
        }
    }, [value]);

    // Memoizar filteredOptions para evitar recálculo em cada render
    const filteredOptions = useMemo(() => {
        // Filtrar opções que já foram selecionadas
        const availableOptions = options.filter(option => !selectedValues.includes(option.value));

        // Se não há texto digitado, retornar todas as opções disponíveis
        if (!inputText.trim()) {
            return availableOptions;
        }

        // Normalizar o texto de busca
        const normalizedSearchText = normalizeText(inputText.trim());

        // Filtrar opções que correspondem ao texto digitado (independente de maiúsculas, minúsculas e acentos)
        return availableOptions.filter(option => {
            const normalizedLabel = normalizeText(option.label);
            const normalizedValue = normalizeText(option.value);
            return normalizedLabel.includes(normalizedSearchText) || normalizedValue.includes(normalizedSearchText);
        });
    }, [options, selectedValues, inputText]);

    // Memoizar hasExactMatch para evitar recálculo desnecessário
    const hasExactMatch = useMemo(() => {
        if (!inputText.trim()) return false;
        const normalizedSearchText = normalizeText(inputText.trim());
        return filteredOptions.some(option => {
            const normalizedLabel = normalizeText(option.label);
            const normalizedValue = normalizeText(option.value);
            return normalizedLabel === normalizedSearchText || normalizedValue === normalizedSearchText;
        });
    }, [inputText, filteredOptions]);

    // Memoizar shouldShowAddOption para estabilidade nas dependências do useEffect
    const shouldShowAddOption = useMemo(() => {
        return allowNewValues && inputText.trim() && !hasExactMatch && !isMaxItemsReached;
    }, [allowNewValues, inputText, hasExactMatch, isMaxItemsReached]);

    // Mostrar autocomplete quando o input está focado e há opções disponíveis ou item de adicionar
    useEffect(() => {
        if (!isFocused || isMaxItemsReached) {
            setAutocompleteOpen(false);
            setHighlightedIndex(-1);
            return;
        }

        // Mostrar autocomplete quando focado, mesmo sem texto (mostra todas as opções disponíveis)
        const hasOptions = filteredOptions.length > 0;
        const shouldShow = hasOptions || shouldShowAddOption;

        // Sempre mostrar quando focado e há opções disponíveis
        if (shouldShow) {
            setAutocompleteOpen(true);
        } else {
            setAutocompleteOpen(false);
        }

        // Resetar índice destacado quando o texto mudar ou quando fechar
        if (!shouldShow) {
            setHighlightedIndex(-1);
        } else {
            const totalItems = filteredOptions.length + (shouldShowAddOption ? 1 : 0);
            if (highlightedIndex >= totalItems) {
                setHighlightedIndex(-1);
            }
        }
    }, [isFocused, inputText, filteredOptions.length, shouldShowAddOption, isMaxItemsReached]);

    const handleAddValue = (newValue, shouldCloseAutocomplete = true) => {
        if (newValue === null || newValue === undefined || (typeof newValue === 'string' && newValue.trim() === "")) return;

        // Verificar se o limite de itens foi atingido
        if (isMaxItemsReached) {
            return;
        }

        const trimmedValue = typeof newValue === 'string' ? newValue.trim() : newValue;

        // Verificar se o valor já não está selecionado
        if (selectedValues.includes(trimmedValue)) {
            setInputText("");
            if (shouldCloseAutocomplete) {
                setAutocompleteOpen(false);
            }
            return;
        }

        const newSelectedValues = [...selectedValues, trimmedValue];
        setSelectedValues(newSelectedValues);
        onInput(newSelectedValues);
        setInputText("");

        // Fechar autocomplete apenas se solicitado ou se o limite foi atingido
        const shouldClose = shouldCloseAutocomplete || (maxItems !== undefined && newSelectedValues.length >= maxItems);
        if (shouldClose) {
            setAutocompleteOpen(false);
            // Se foi fechado por clique, também perder o foco
            if (shouldCloseAutocomplete) {
                setIsFocused(false);
            }
        } else {
            // Se não deve fechar, garantir que o autocomplete permaneça aberto
            // O useEffect vai cuidar disso baseado no isFocused e nas opções disponíveis
        }
    };

    const handleRemoveValue = (valueToRemove) => {
        const newSelectedValues = selectedValues.filter(v => v !== valueToRemove);
        setSelectedValues(newSelectedValues);
        onInput(newSelectedValues);
        // Não abrir o autocomplete quando um item é removido
        // Garantir que o autocomplete permaneça fechado ao remover um item
        if (!isFocused) {
            setAutocompleteOpen(false);
        }
    };

    const handleInputChange = (newValue) => {
        setInputText(newValue);
        // Resetar índice destacado quando o texto mudar
        setHighlightedIndex(-1);
    };

    const handleKeyDown = (e) => {
        const totalItems = filteredOptions.length + (shouldShowAddOption ? 1 : 0);

        if (e.key === 'Enter') {
            e.preventDefault();

            // Se há uma opção destacada, selecionar ela (não fechar autocomplete)
            if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                handleAddValue(filteredOptions[highlightedIndex].value, false);
                setHighlightedIndex(-1);
                return;
            }

            // Se o item "Adicionar" está destacado
            if (highlightedIndex === filteredOptions.length && shouldShowAddOption) {
                handleAddValue(inputText, false);
                setHighlightedIndex(-1);
                return;
            }

            // Se há opções filtradas e o primeiro corresponde exatamente ao texto, selecionar ele
            if (filteredOptions.length > 0) {
                const normalizedSearchText = normalizeText(inputText.trim());
                const exactMatch = filteredOptions.find(opt => {
                    const normalizedLabel = normalizeText(opt.label);
                    const normalizedValue = normalizeText(opt.value);
                    return normalizedLabel === normalizedSearchText || normalizedValue === normalizedSearchText;
                });

                if (exactMatch) {
                    handleAddValue(exactMatch.value, false);
                    return;
                }
            }

            // Se allowNewValues está ativo, adicionar o valor digitado
            if (allowNewValues && inputText.trim()) {
                handleAddValue(inputText, false);
            }
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (totalItems > 0) {
                setAutocompleteOpen(true);
                setHighlightedIndex(prev => {
                    if (prev < 0) {
                        return 0; // Primeira vez, começar no primeiro item
                    }
                    if (prev < totalItems - 1) {
                        return prev + 1;
                    }
                    return 0; // Voltar para o início
                });
            }
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (totalItems > 0) {
                setAutocompleteOpen(true);
                setHighlightedIndex(prev => {
                    if (prev < 0) {
                        return totalItems - 1; // Primeira vez, começar no último item
                    }
                    if (prev > 0) {
                        return prev - 1;
                    }
                    return totalItems - 1; // Ir para o final
                });
            }
        } else if (e.key === 'Escape') {
            setAutocompleteOpen(false);
            setHighlightedIndex(-1);
            inputRef.current?.blur();
        }
    };

    const handleOptionClick = async (option) => {
        // Marcar que uma opção foi clicada para evitar que o blur feche o autocomplete
        optionClickedRef.current = true;

        // Quando clicado, adicionar valor SEM fechar o autocomplete (false)
        handleAddValue(option.value, false);

        // Garantir que o foco permaneça no input
        if (inputRef.current) {
            inputRef.current.focus();
        }

        // Resetar a flag após um pequeno delay
        setTimeout(() => {
            optionClickedRef.current = false;
        }, 100);
    };

    const handleFocus = () => {
        if (!isMaxItemsReached) {
            // Limpar qualquer timeout de blur pendente
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
                blurTimeoutRef.current = null;
            }
            isFocusedRef.current = true;
            setIsFocused(true);
            // Quando o input recebe foco, o useEffect vai abrir o autocomplete
            // se houver opções disponíveis (mesmo sem texto digitado)
        }
    };

    const handleBlur = () => {
        // Limpar timeout anterior se existir
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
        }

        // Delay para permitir clique no autocomplete
        blurTimeoutRef.current = setTimeout(() => {
            // Não fechar se uma opção foi clicada (o handleOptionClick já fechou)
            // Não fechar se o input voltou a ter foco
            if (!optionClickedRef.current && !isFocusedRef.current) {
                setIsFocused(false);
                setAutocompleteOpen(false);
            }
            optionClickedRef.current = false; // Resetar a flag
        }, 200);
    };

    const getPlaceholder = () => {
        if (allowNewValues) {
            return placeholder || t('input.selectOptions.searchOrAddPlaceholder');
        }
        return placeholder || t('input.selectOptions.searchPlaceholder');
    };

    // Preparar opções para o autocomplete
    const autocompleteOptions = filteredOptions.map((option, index) => ({
        id: `option-${index}`,
        label: option.label,
        value: option.value,
        icon: option.icon,
        variant: option.variant,
        disabled: option.disabled,
        onClick: async () => {
            await handleOptionClick(option);
        }
    }));

    // Adicionar item "Adicionar" no final se necessário
    if (shouldShowAddOption) {
        autocompleteOptions.push({
            id: 'add-new-option',
            label: t('input.selectOptions.addOption'),
            value: inputText.trim(),
            icon: <KeyboardCommandKeyRoundedIcon />,
            variant: 'default',
            disabled: false,
            isAddOption: true,
            onClick: async () => {
                handleAddValue(inputText);
            }
        });
    }

    return (
        <div className={`CustomInput_container SelectOptionsInput-container ${className || ''}`.trim()} ref={containerRef}>
            <div className="SelectOptionsInput-inner">
                <BaseInput
                    ref={inputRef}
                    autoScroll={autoScroll}
                    {...props}
                    value={inputText}
                    onInput={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    label={label}
                    placeholder={getPlaceholder()}
                    variant={variant}
                    disabled={isMaxItemsReached || props.disabled}
                />

                {selectedValues.length > 0 && (
                    <div className="input-badges-container">
                        {(() => {
                            const shouldCollapse = collapseInAmount !== null &&
                                selectedValues.length > collapseInAmount &&
                                !isFocused &&
                                !manuallyExpanded;

                            const visibleCount = shouldCollapse ? collapseInAmount : selectedValues.length;
                            const visibleValues = selectedValues.slice(0, visibleCount);
                            const hiddenCount = selectedValues.length - visibleCount;

                            return (
                                <>
                                    {visibleValues.map((selectedValue) => {
                                        // Encontrar o label correspondente na lista de opções
                                        const option = options.find(opt => opt.value === selectedValue);
                                        const displayLabel = option ? option.label : selectedValue;

                                        return (
                                            <div key={selectedValue} className="input-badge">
                                                <span>{displayLabel}</span>
                                                <button
                                                    type="button"
                                                    className="badge-remove-icon"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRemoveValue(selectedValue);
                                                    }}
                                                >
                                                    <CloseRoundedIcon />
                                                </button>
                                            </div>
                                        );
                                    })}

                                    {/* Toggle Button for blurred state */}
                                    {collapseInAmount !== null && !isFocused && selectedValues.length > collapseInAmount && (
                                        <div className="collapse-toggle-row" style={{ width: '100%', marginTop: '4px', marginBottom: '4px' }}>
                                            {hiddenCount > 0 ? (
                                                <span style={{ fontSize: '0.85em', color: '#666' }}>
                                                    {t('input.selectOptions.moreSelected', { count: hiddenCount })} <span
                                                        onClick={(e) => { e.stopPropagation(); setManuallyExpanded(true); }}
                                                        style={{ cursor: 'pointer', color: 'var(--primary-color, #1976d2)', fontWeight: 600 }}
                                                    >
                                                        {t('input.selectOptions.showAll')}
                                                    </span>
                                                </span>
                                            ) : (
                                                <span
                                                    onClick={(e) => { e.stopPropagation(); setManuallyExpanded(false); }}
                                                    style={{ cursor: 'pointer', color: 'var(--primary-color, #1976d2)', fontSize: '0.85em', fontWeight: 600 }}
                                                >
                                                    {t('input.selectOptions.hide')}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                )}

                <InputAutoCompleteCard
                    open={autocompleteOpen}
                    options={autocompleteOptions}
                    containerRef={containerRef}
                    onClose={() => {
                        // Fechar o autocomplete e perder o foco quando clicar fora
                        isFocusedRef.current = false;
                        setIsFocused(false);
                        setAutocompleteOpen(false);
                        setHighlightedIndex(-1);
                    }}
                    onOptionClick={handleOptionClick}
                    highlightedIndex={highlightedIndex}
                />
            </div>
        </div>
    );
}

export default SelectOptionsInput;
